import { LocalizedText, emptyLocalizedText, DEFAULT_LANGUAGE_CODES } from '../types/LocalizedText';
import { AppLanguage } from '../types/Language';

export function getLanguageCodes(
  languages?: AppLanguage[],
  fallback: string[] = [...DEFAULT_LANGUAGE_CODES]
): string[] {
  if (!languages || languages.length === 0) {
    return fallback;
  }

  return languages
    .filter((language) => language.isActive !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((language) => language.code);
}

export function normalizeLocalizedText(
  value: LocalizedText | string | undefined | null,
  languageCodes: string[] = [...DEFAULT_LANGUAGE_CODES]
): LocalizedText {
  const empty = emptyLocalizedText(languageCodes);

  if (!value) {
    return empty;
  }

  if (typeof value === 'string') {
    return Object.fromEntries(languageCodes.map((code) => [code, value]));
  }

  return { ...empty, ...value };
}

export function getLocalizedText(
  value: LocalizedText | string | undefined | null,
  languageCode: string,
  languageCodes: string[] = [...DEFAULT_LANGUAGE_CODES]
): string {
  const normalized = normalizeLocalizedText(value, languageCodes);

  if (normalized[languageCode]?.trim()) {
    return normalized[languageCode];
  }

  for (const code of languageCodes) {
    if (normalized[code]?.trim()) {
      return normalized[code];
    }
  }

  return Object.values(normalized).find((text) => text.trim()) ?? '';
}

export function formatLocalizedTextDisplay(
  value: LocalizedText | string | undefined | null,
  languageCodes?: string[]
): string {
  const normalized =
    typeof value === 'string' || !value
      ? normalizeLocalizedText(value, languageCodes)
      : value;

  const texts = (languageCodes ?? Object.keys(normalized))
    .map((code) => normalized[code]?.trim())
    .filter(Boolean);

  const uniqueTexts = Array.from(new Set(texts));

  if (uniqueTexts.length > 1) {
    return uniqueTexts.join(' / ');
  }

  return uniqueTexts[0] || '-';
}

export function hasLocalizedText(value: LocalizedText | string | undefined | null): boolean {
  if (!value) {
    return false;
  }

  if (typeof value === 'string') {
    return Boolean(value.trim());
  }

  return Object.values(value).some((text) => text?.trim());
}

export function trimLocalizedText(value: LocalizedText): LocalizedText {
  return Object.fromEntries(
    Object.entries(value).map(([code, text]) => [code, text.trim()])
  );
}

export function serializeLocalizedText(
  value: LocalizedText,
  languages: AppLanguage[],
  options?: { omitEmptyOptional?: boolean }
): LocalizedText {
  const trimmed = trimLocalizedText(value);

  if (!options?.omitEmptyOptional) {
    return trimmed;
  }

  const result: LocalizedText = {};

  languages
    .filter((language) => language.isActive !== false)
    .forEach((language) => {
      const text = trimmed[language.code] ?? '';
      if (language.isRequired !== false || text) {
        result[language.code] = text;
      }
    });

  return result;
}

export function getMissingRequiredLanguageLabels(
  value: LocalizedText,
  languages: AppLanguage[]
): string[] {
  return languages
    .filter(
      (language) =>
        language.isActive !== false &&
        language.isRequired !== false &&
        !value[language.code]?.trim()
    )
    .map((language) => language.label);
}
